import { createContext, useContext, useEffect, useState, type DependencyList } from "react";

import { PortalError, type Portal, type Viewer } from "./api/client";
import { poll } from "./polling";

export interface Session {
  api: Portal;
  viewer: Viewer;
}

export const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error("useSession outside a SessionContext provider");
  }
  return session;
}

export interface Resource<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
}

/** How often a page re-reads what it is showing, while it is being looked at. */
const REREAD_MS = 5_000;

/**
 * Reads one resource for as long as its dependencies hold, and keeps reading
 * it. A request answered in seconds is worthless behind a page that never asks
 * again, so this re-reads while the tab is visible and once more as soon as it
 * becomes visible: a hidden tab costs nothing and is stale only until it is
 * looked at.
 *
 * Changing identity clears the previous answer immediately. A transient error
 * retains the last answer only for the same identity. Reads never overlap.
 */
export function useResource<T>(
  load: (signal: AbortSignal) => Promise<T>,
  deps: DependencyList,
  again?: (value: T) => boolean,
  interval = REREAD_MS,
): Resource<T> {
  const [state, setState] = useState<Resource<T> & { identity: DependencyList }>({
    data: null, error: null, loading: true, identity: deps,
  });
  useEffect(() => {
    return poll({
      load,
      accept: (data) => setState({ data, error: null, loading: false, identity: deps }),
      reject: (cause) => setState((shown) => ({
        data: sameIdentity(shown.identity, deps) ? shown.data : null,
        error: cause instanceof Error ? cause : new PortalError(0, "the portal is unreachable"),
        loading: false,
        identity: deps,
      })),
      visible: () => document.visibilityState === "visible",
      subscribe: (wake) => {
        document.addEventListener("visibilitychange", wake);
        return () => document.removeEventListener("visibilitychange", wake);
      },
      interval,
      again,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, interval]);
  return sameIdentity(state.identity, deps) ? state : { data: null, error: null, loading: true };
}

function sameIdentity(left: DependencyList, right: DependencyList) {
  return left.length === right.length && left.every((value, index) => Object.is(value, right[index]));
}
